import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button as MuiButton,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import {
  fetchAstroTickets,
  deleteAstroTicket,
} from "../../Redux/Reducers/AstroTicketReducer";
import socket from "../../socket/socket";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissions } from "../../context/AuthContext";

function AstrologerSupportList() {
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const pagination = useSelector((state) => state?.AstroTicketReducer?.pagination) || {};
  const DataAstro = useSelector((state) => state.AstroTicketReducer?.data || []);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("open");
  const [isConnected, setIsConnected] = useState(socket.connected);

  useEffect(() => {
    const senderData = JSON.parse(localStorage.getItem("vedhvidhi-admin-data"));
    const senderId = senderData?._id;

    function onConnect() {
      setIsConnected(true);
      if (senderId) {
        socket.emit("new user", senderId);
      }
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, []);

  useEffect(() => {
    const handleNewTicket = (newTicketData) => {
      if (activeTab === "open") {
        toast.info("A new ticket has arrived! Updating list...");
        const { page, pageSize } = paginationModel;
        dispatch(fetchAstroTickets({ page: page + 1, limit: pageSize, status: "active", userType: "astro" }));
      }
    };

    socket.on('new ticket', handleNewTicket);
    return () => {
      socket.off('new ticket', handleNewTicket);
    };
  }, [dispatch, activeTab, paginationModel]);

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    const status = activeTab === "open" ? "active" : "close";
    dispatch(fetchAstroTickets({ page: page + 1, limit: pageSize, status, userType: "astro" }));
  }, [paginationModel, dispatch, activeTab]);

  const handleClickOpen = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFeedbackId(null);
  };

  const handleDelete = async () => {
    if (selectedFeedbackId) {
      await dispatch(deleteAstroTicket(selectedFeedbackId));
      const { page, pageSize } = paginationModel;
      const status = activeTab === "open" ? "active" : "close";
      dispatch(fetchAstroTickets({ page: page + 1, limit: pageSize, status, userType: "astro" }));
    }
    handleClose();
  };

  const filteredData =
    DataAstro?.filter((feedback) => {
      if (activeTab === "open") {
        return feedback?.status === "active";
      } else if (activeTab === "close") {
        return feedback?.status === "close";
      }
      return false;
    }) || [];

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "ticketId", headerName: "Ticket Number", width: 120 },
    { field: "userName", headerName: "User Name", width: 200 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    {
      field: "feedback",
      headerName: "Feedback",
      width: 250,
      renderCell: ({ row }) => (
        <span>
          {row.feedback.length > 25
            ? row.feedback.substring(0, 25) + "..."
            : row.feedback}
        </span>
      ),
    },
    { field: 'createdAt', headerName: 'Created At', width: 200 },
    { field: 'updatedAt', headerName: 'Updated At', width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: ({ row }) => (
        <div>
          {can('astrologer_tickets:reply') && (
            <Tooltip title="View/Reply to Ticket">
              <IconButton component={Link} to={`/AstroSupportChat/${row.id}`}>
                <i className="icofont-eye text-info"></i>
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={() => handleClickOpen(row.id)}>
            <i className="icofont-ui-delete text-danger"></i>
          </IconButton>
        </div>
      ),
    },
  ];

  const rows = filteredData?.map((feedback, index) => ({
    id: feedback?._id || index.toString(),
    sono: paginationModel.page * paginationModel.pageSize + index + 1,
    userName: feedback?.fullName || "N/A",
    email: feedback?.email || "N/A",
    phoneNumber: feedback?.mobile || "N/A",
    feedback: feedback?.description || "No feedback provided",
    ticketId: feedback?.ticketId || "N/A",
    status: feedback?.status === "active" ? "Open" : "Closed",
    createdAt: feedback?.createdAt
      ? new Date(feedback?.createdAt).toLocaleString()
      : "",
    updatedAt: feedback?.updatedAt ? new Date(feedback?.updatedAt).toLocaleString()
      : "",
  })) || [];

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      <div className="body d-flex py-1">
        <div className="container-xxl">
          <PageHeader1 pagetitle="Astrologer Ticket List" />
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Ticket Status Tabs"
            >
              <Tab label="Open Tickets" value="open" />
              <Tab label="Closed Tickets" value="close" />
            </Tabs>
          </Box>
          <div className="row g-0 mb-3">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <Box sx={{ width: "100%", height: 650 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{ toolbar: { showQuickFilter: true } }}
                      paginationMode="server"
                      rowCount={pagination?.totalDocs || 0}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                    />
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this Astrologer Support?
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={handleClose}>
              Cancel
            </MuiButton>
            <MuiButton onClick={handleDelete} color="error">
              Delete
            </MuiButton>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}

export default AstrologerSupportList;